import {
  Button,
  Dropdown,
  IconButton,
  InlineNotification,
  NumberInput,
  SkeletonText,
  Stack,
  TextInput,
  Toggle,
} from '@carbon/react';
import { Add, TrashCan } from '@carbon/icons-react';
import { hasPermission, useAdmin, type RecordValues } from '@pos/admin-ui';
import type { HoComponents } from '@pos/api-client';
import { useTranslation } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import { asProblemMessage } from '../sync/problem';
import './reservations.scss';

type Settings = HoComponents['schemas']['ReservationSettingsDto'];
type Window = HoComponents['schemas']['ReservationWindowDto'];

/** ISO weekdays, Monday first. */
const DAYS = [1, 2, 3, 4, 5, 6, 7] as const;
const SLOT_MINUTES = [15, 30, 60] as const;

const key = (storeId: string) => ['ho', 'store-reservations', storeId] as const;

async function unwrap<T>(call: Promise<{ data?: T; error?: unknown }>): Promise<T> {
  const { data, error } = await call;
  if (data === undefined) throw error;
  return data;
}

/** The editable form: limits may be empty, meaning no limit. */
interface Draft {
  enabled: boolean;
  slotMinutes: number;
  daysAhead: number;
  maxParty: number;
  slotBookings: number | '';
  slotGuests: number | '';
  acceptMinutes: number;
  hours: Window[];
}

const toDraft = (s: Settings): Draft => ({
  enabled: s.enabled,
  slotMinutes: Number(s.slotMinutes),
  daysAhead: Number(s.daysAhead),
  maxParty: Number(s.maxParty),
  slotBookings: s.slotBookings === null ? '' : Number(s.slotBookings),
  slotGuests: s.slotGuests === null ? '' : Number(s.slotGuests),
  acceptMinutes: Number(s.acceptMinutes),
  hours: s.hours.map((w) => ({ ...w })),
});

/**
 * A store's table reservations (ADR 0092; FR-RES-02): one set of rules for every channel and the POS. Switched on here, with the slot
 * length, how far ahead, the largest party, the most bookings and guests a slot holds, the time the store has to answer an online
 * booking, and when tables can be booked (several windows a day; none: the store's opening hours). Saved together; like other store
 * setup, it reaches the store and the website when master data is published (ADR 0078).
 */
export function StoreReservationsTab({ store }: { store: RecordValues }) {
  const { t } = useTranslation();
  const storeId = String(store.id);
  const settings = useQuery({
    queryKey: key(storeId),
    queryFn: () => unwrap(hoApi.GET('/api/v1/tables/stores/{storeId}/reservation-settings', { params: { path: { storeId } } })),
  });

  if (settings.isLoading) return <SkeletonText paragraph lineCount={4} />;
  if (settings.error || !settings.data)
    return <InlineNotification kind="error" lowContrast hideCloseButton title={asProblemMessage(t, settings.error)} />;
  return <ReservationsForm key={storeId} storeId={storeId} settings={settings.data} />;
}

function ReservationsForm({ storeId, settings }: { storeId: string; settings: Settings }) {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const canManage = hasPermission(session, 'ho.tables.reservation_settings.manage');
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft>(() => toDraft(settings));
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        hoApi.PUT('/api/v1/tables/stores/{storeId}/reservation-settings', {
          params: { path: { storeId } },
          body: {
            ...draft,
            slotBookings: draft.slotBookings === '' ? null : draft.slotBookings,
            slotGuests: draft.slotGuests === '' ? null : draft.slotGuests,
          },
        }),
      ),
    onMutate: () => setSaved(false),
    onSuccess: (next) => {
      queryClient.setQueryData(key(storeId), next);
      setDraft(toDraft(next));
      setSaved(true);
    },
  });
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const change = (index: number, patch: Partial<Window>) =>
    set({ hours: draft.hours.map((w, i) => (i === index ? { ...w, ...patch } : w)) });
  const numberField = (
    field: 'daysAhead' | 'maxParty' | 'acceptMinutes' | 'slotBookings' | 'slotGuests',
    min: number,
    max: number,
    optional = false,
  ) => (
    <NumberInput
      id={`reservations-${field}`}
      label={t(`reservations.fields.${field}`)}
      helperText={optional ? t('reservations.fields.noLimit') : undefined}
      min={min}
      max={max}
      allowEmpty={optional}
      value={draft[field]}
      disabled={!canManage}
      onChange={(_e, { value }) => set({ [field]: value === '' || value === undefined ? (optional ? '' : min) : Number(value) })}
    />
  );

  return (
    <Stack gap={6} className="pos-store-reservations">
      <p className="pos-store-reservations__intro">{t('reservations.intro')}</p>
      {save.error && (
        <InlineNotification kind="error" lowContrast title={asProblemMessage(t, save.error)} onCloseButtonClick={() => save.reset()} />
      )}
      {saved && (
        <InlineNotification kind="success" lowContrast title={t('reservations.saved')} onCloseButtonClick={() => setSaved(false)} />
      )}
      <Toggle
        id="reservations-enabled"
        labelText={t('reservations.fields.enabled')}
        labelA={t('reservations.fields.off')}
        labelB={t('reservations.fields.on')}
        toggled={draft.enabled}
        disabled={!canManage}
        onToggle={(on) => set({ enabled: on })}
      />
      <div className="pos-store-reservations__fields">
        <Dropdown
          id="reservations-slot"
          titleText={t('reservations.fields.slotMinutes')}
          label={t('reservations.fields.slotMinutes')}
          items={[...SLOT_MINUTES]}
          itemToString={(m: number | null) => (m ? t('reservations.minutes', { count: m }) : '')}
          selectedItem={draft.slotMinutes}
          disabled={!canManage}
          onChange={({ selectedItem }: { selectedItem: number | null }) => selectedItem && set({ slotMinutes: selectedItem })}
        />
        {numberField('daysAhead', 0, 60)}
        {numberField('maxParty', 1, 200)}
        {numberField('slotBookings', 1, 10000, true)}
        {numberField('slotGuests', 1, 10000, true)}
        {numberField('acceptMinutes', 5, 1440)}
      </div>
      <section aria-label={t('reservations.hours')}>
        <h3 className="pos-store-reservations__title">{t('reservations.hours')}</h3>
        <p className="pos-store-reservations__intro">{t('reservations.hoursHelp')}</p>
        <div className="pos-store-reservations__days">
          {DAYS.map((day) => (
            <div key={day} className="pos-store-reservations__day">
              <span className="pos-store-reservations__day-name">{t(`md.enums.day_of_week.${day}` as 'md.enums.day_of_week.1')}</span>
              <div className="pos-store-reservations__windows">
                {draft.hours.map((w, index) =>
                  Number(w.dayOfWeek) !== day ? null : (
                    <div key={index} className="pos-store-reservations__window">
                      <TextInput
                        id={`reservations-opens-${index}`}
                        type="time"
                        size="sm"
                        labelText={t('reservations.opens')}
                        hideLabel
                        value={w.opensAt}
                        disabled={!canManage}
                        onChange={(e) => change(index, { opensAt: e.target.value })}
                      />
                      <span aria-hidden="true">–</span>
                      <TextInput
                        id={`reservations-closes-${index}`}
                        type="time"
                        size="sm"
                        labelText={t('reservations.closes')}
                        hideLabel
                        value={w.closesAt}
                        disabled={!canManage}
                        onChange={(e) => change(index, { closesAt: e.target.value })}
                      />
                      <IconButton
                        kind="ghost"
                        size="sm"
                        label={t('reservations.removeWindow')}
                        disabled={!canManage}
                        onClick={() => set({ hours: draft.hours.filter((_, i) => i !== index) })}
                      >
                        <TrashCan />
                      </IconButton>
                    </div>
                  ),
                )}
                {!draft.hours.some((w) => Number(w.dayOfWeek) === day) && (
                  <span className="pos-store-reservations__none">
                    {draft.hours.length ? t('reservations.noBookingsThisDay') : t('reservations.openingHoursThisDay')}
                  </span>
                )}
                <Button
                  kind="ghost"
                  size="sm"
                  renderIcon={Add}
                  disabled={!canManage}
                  onClick={() => set({ hours: [...draft.hours, { dayOfWeek: day, opensAt: '11:00', closesAt: '21:00' }] })}
                >
                  {t('reservations.addWindow')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>
      <div>
        <Button kind="primary" disabled={!canManage || save.isPending} onClick={() => save.mutate()}>
          {t('admin.save')}
        </Button>
      </div>
    </Stack>
  );
}
